import { readonly, ref, useContext } from '@nuxtjs/composition-api';

import type { ComposableFunctionArgs, Countries } from '~/composables/types';
import useApi from '~/composables/useApi';
import countryInformationGlobal from '~/diptyqueTheme/customQueries/magento/countryInformationGlobal';
import { Logger } from '~/helpers/logger';
import type { Country, Maybe } from '~/modules/GraphQL/types';

import type { UseCountrySearchInterface } from './useCountrySearch';

/**
 * Allows fetching a list of countries or a single country by ID
 *
 * See the {@link UseCountrySearchInterface} for a list of methods and values available in this composable.
 */
export function useCountrySearch(): UseCountrySearchInterface {
  const { app } = useContext();
  const { cacheableQuery } = useApi();
  const loading = ref(false);
  const error = ref({
    search: null,
    globalSearch: null,
    load: null
  });

  const search = async (params: ComposableFunctionArgs<{ id: string }>): Promise<Maybe<Country>> => {
    let results = null;

    try {
      loading.value = true;

      Logger.debug('[Magento]: Search country information based on', { params });

      const { data } = await app.$vsf.$magento.api.country(params.id, params?.customQuery ?? null);

      Logger.debug('[Result]:', { data });

      results = data?.country ?? null;
      error.value.search = null;
    } catch (err) {
      error.value.search = err;
      Logger.error('useCountrySearch/search', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  const globalSearch = async (params: ComposableFunctionArgs<{ id: string }>): Promise<Maybe<Country>> => {
    let result: Country | null = null;

    try {
      loading.value = true;

      Logger.debug('[Magento]: Search global country information based on', { params });

      const { data } = await cacheableQuery<{ globalScopeCountry?: Country | null }>(countryInformationGlobal, params);

      Logger.debug('[Result]:', { data });

      result = data?.globalScopeCountry ?? null;
      error.value.globalSearch = null;
    } catch (err) {
      error.value.globalSearch = err;
      Logger.error('useCountrySearch/globalSearch', err);
    } finally {
      loading.value = false;
    }

    return result;
  };

  const load = async (): Promise<Array<Countries>> => {
    let results = [];

    try {
      loading.value = true;

      Logger.debug('[Magento]: Load available countries on store');
      const { data } = await app.context.$vsf.$magento.api.countries();
      Logger.debug('[Countries Result]:', { data });

      results = data?.countries ?? [];
      error.value.search = null;
    } catch (err) {
      error.value.search = err;
      Logger.error('useCountrySearch/load', err);
    } finally {
      loading.value = false;
    }

    return results;
  };

  return {
    load,
    globalSearch,
    search,
    loading: readonly(loading),
    error: readonly(error)
  };
}

export * from './useCountrySearch';
export default useCountrySearch;
